import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from 'amazon-cognito-identity-js';
import { useAuth } from './contexts/AuthContext';
import { Header, Card, Button, Input, CrownedELXR } from './components/ui';
import { tokens } from './styles/tokens';
import { useResponsive } from './hooks/useResponsive';
import responsiveStyles from './styles/responsive.module.css';

// Cognito pool setup
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
};

function LandingPage() {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  
  // Responsive hook for mobile detection
  const { isMobile } = useResponsive();
  
  // Show success message if coming from verification
  const [successMessage, setSuccessMessage] = useState('');

  // Create user pool instance only if we have the required values
  let userPool = null;
  if (poolData.UserPoolId && poolData.ClientId) {
    userPool = new CognitoUserPool(poolData);
  } else {
    console.error('Missing required Cognito configuration:', poolData);
  }

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
    // Clear errors when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
    if (errors.general) {
      setErrors({ ...errors, general: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!credentials.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(credentials.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!credentials.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!userPool) {
      setErrors({ general: 'AWS Cognito configuration is missing. Please check your environment variables.' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const userData = { Username: credentials.email, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);
      
      const authDetails = new AuthenticationDetails({
        Username: credentials.email,
        Password: credentials.password
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (userSession) => {
          console.log('Sign in successful:', userSession);
          setLoading(false);
          
          // Use the AuthContext login function
          login(userSession, cognitoUser);
          
          // Navigate to home page
          navigate('/home');
        },
        onFailure: (err) => {
          console.error('Sign in error:', err);
          setLoading(false);
          
          // Handle different types of Cognito errors
          let errorMessage = 'An error occurred during sign in. Please try again.';
          
          if (err.name === 'NotAuthorizedException') {
            errorMessage = 'Incorrect email or password.';
            setErrors({ general: errorMessage });
          } else if (err.name === 'UserNotConfirmedException') {
            errorMessage = 'Please verify your email before signing in.';
            setErrors({ general: errorMessage });
            // Navigate to verification page
            navigate('/verify-email', { 
              state: { 
                email: credentials.email,
                message: errorMessage
              }
            });
          } else if (err.name === 'UserNotFoundException') {
            errorMessage = 'No account found with this email.';
            setErrors({ email: errorMessage });
          } else if (err.name === 'InvalidParameterException') {
            errorMessage = 'Invalid email or password format.';
            setErrors({ general: errorMessage });
          } else {
            setErrors({ general: err.message || errorMessage });
          }
        }
      });

    } catch (error) {
      console.error('Unexpected sign in error:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
      setLoading(false);
    }
  };

  return (
    <div 
      className={isMobile ? responsiveStyles.loginMobileContainer : ''}
      style={{
        minHeight: '100vh',
        background: tokens.colors.gradients.dark,
        display: 'flex',
        position: 'relative',
        overflow: 'hidden'
      }}>
      {/* Animated Background Pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `radial-gradient(circle at 20% 80%, ${tokens.colors.accent[900]}22 0%, transparent 50%),
                     radial-gradient(circle at 80% 20%, ${tokens.colors.primary[900]}22 0%, transparent 50%),
                     radial-gradient(circle at 40% 40%, ${tokens.colors.accent[800]}11 0%, transparent 50%)`,
        animation: 'float 20s ease-in-out infinite'
      }} />
      
      {/* Grid Pattern Overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `linear-gradient(${tokens.colors.neutral[800]}11 1px, transparent 1px),
                          linear-gradient(90deg, ${tokens.colors.neutral[800]}11 1px, transparent 1px)`,
        backgroundSize: '50px 50px',
        opacity: 0.3
      }} />

      {/* Left Side - Branding */}
      <div 
        className={isMobile ? responsiveStyles.loginMobileBranding : ''}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: tokens.spacing[12],
          position: 'relative',
          background: `linear-gradient(135deg, ${tokens.colors.dark[900]}00 0%, ${tokens.colors.dark[900]}99 100%)`
        }}>
        <div style={{ maxWidth: '600px', textAlign: 'center' }}>
          <h1 
            className={isMobile ? responsiveStyles.mobileHeading : ''}
            style={{
              fontSize: '5rem',
              fontWeight: tokens.typography.fontWeight.extrabold,
              background: tokens.colors.gradients.glow,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: tokens.spacing[6],
              filter: 'drop-shadow(0 0 60px rgba(34, 197, 94, 0.4))',
              animation: 'glow 3s ease-in-out infinite alternate'
            }}>
            <CrownedELXR /><span style={{ marginLeft: '0em' }}> Sportsbook</span>
          </h1>
          
          <p 
            className={isMobile ? responsiveStyles.mobileSubheading : ''}
            style={{
              fontSize: tokens.typography.fontSize['2xl'],
              color: tokens.colors.neutral[300],
              marginBottom: tokens.spacing[8],
              lineHeight: 1.6
            }}>
            Experience the future of sports betting with XRP
          </p>

          <div 
            className={`${isMobile ? responsiveStyles.mobileStats : ''} ${isMobile ? responsiveStyles.mobileStackedStats : ''}`}
            style={{
              display: 'flex',
              gap: tokens.spacing[8],
              justifyContent: 'center',
              marginTop: tokens.spacing[12]
            }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: tokens.typography.fontSize['3xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: tokens.colors.accent[400]
              }}>
                <span style={{ animation: 'countUp 2s ease-out' }}>5+</span>
              </div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm }}>
                Sports Markets
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: tokens.typography.fontSize['3xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: tokens.colors.accent[400]
              }}>
                <span style={{ animation: 'countUp 2s ease-out 0.5s both' }}>24/7</span>
              </div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm }}>
                Platform Uptime
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: tokens.typography.fontSize['3xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: tokens.colors.accent[400]
              }}>
                <span style={{ animation: 'countUp 2s ease-out 1s both' }}>3-5s</span>
              </div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm }}>
                Settlement Time
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div 
        className={isMobile ? responsiveStyles.loginMobileForm : ''}
        style={{
          width: '480px',
          background: `${tokens.colors.dark[800]}99`,
          backdropFilter: 'blur(20px)',
          borderLeft: `1px solid ${tokens.colors.neutral[800]}`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: tokens.spacing[12],
          position: 'relative'
        }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: tokens.colors.gradients.glow,
          filter: 'blur(100px)',
          opacity: 0.2
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 
            className={isMobile ? responsiveStyles.mobileFormTitle : ''}
            style={{
              fontSize: tokens.typography.fontSize['3xl'],
              fontWeight: tokens.typography.fontWeight.bold,
              color: tokens.colors.neutral[0],
              marginBottom: tokens.spacing[2]
            }}>
            Welcome back
          </h2>
          
          <p style={{
            color: tokens.colors.neutral[400],
            marginBottom: tokens.spacing[8]
          }}>
            Sign in to continue to your account
          </p>

          {successMessage && (
            <div style={{
              padding: tokens.spacing[4],
              background: `linear-gradient(135deg, ${tokens.colors.accent[900]}44 0%, ${tokens.colors.accent[800]}22 100%)`,
              border: `1px solid ${tokens.colors.accent[700]}`,
              borderRadius: tokens.borderRadius.lg,
              color: tokens.colors.accent[400],
              marginBottom: tokens.spacing[6],
              fontSize: tokens.typography.fontSize.sm,
              backdropFilter: 'blur(10px)'
            }}>
              {successMessage}
            </div>
          )}
          
          {errors.general && (
            <div style={{
              padding: tokens.spacing[4],
              background: `linear-gradient(135deg, ${tokens.colors.error[900]}44 0%, ${tokens.colors.error[800]}22 100%)`,
              border: `1px solid ${tokens.colors.error[700]}`,
              borderRadius: tokens.borderRadius.lg,
              color: tokens.colors.error[400],
              marginBottom: tokens.spacing[6],
              fontSize: tokens.typography.fontSize.sm,
              backdropFilter: 'blur(10px)'
            }}>
              {errors.general}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: tokens.spacing[6] }}>
              <label style={{
                display: 'block',
                color: tokens.colors.neutral[300],
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium,
                marginBottom: tokens.spacing[2]
              }}>
                Email address
              </label>
              <div 
                className={isMobile ? responsiveStyles.mobileFormWidth : ''}
                style={{ width: '75%' }}>
                <Input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={credentials.email}
                  onChange={handleChange}
                  autoComplete="email"
                  disabled={loading}
                  error={errors.email}
                  fullWidth={true}
                  style={{
                    background: `${tokens.colors.dark[900]}66`,
                    borderColor: errors.email ? tokens.colors.error[500] : tokens.colors.neutral[700],
                    color: tokens.colors.neutral[0]
                  }}
                />
              </div>
              {errors.email && (
                <span style={{
                  color: tokens.colors.error[400],
                  fontSize: tokens.typography.fontSize.sm,
                  marginTop: tokens.spacing[1],
                  display: 'block'
                }}>
                  {errors.email}
                </span>
              )}
            </div>
            
            <div style={{ marginBottom: tokens.spacing[8] }}>
              <label style={{
                display: 'block',
                color: tokens.colors.neutral[300],
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium,
                marginBottom: tokens.spacing[2]
              }}>
                Password
              </label>
              <div 
                className={isMobile ? responsiveStyles.mobileFormWidth : ''}
                style={{ width: '75%' }}>
                <Input
                  type="password"
                  name="password"
                  placeholder="••••••••"
                  value={credentials.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  disabled={loading}
                  error={errors.password}
                  fullWidth={true}
                  style={{
                    background: `${tokens.colors.dark[900]}66`,
                    borderColor: errors.password ? tokens.colors.error[500] : tokens.colors.neutral[700],
                    color: tokens.colors.neutral[0]
                  }}
                />
              </div>
              {errors.password && (
                <span style={{
                  color: tokens.colors.error[400],
                  fontSize: tokens.typography.fontSize.sm,
                  marginTop: tokens.spacing[1],
                  display: 'block'
                }}>
                  {errors.password}
                </span>
              )}
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              variant="primary"
              size="large"
              style={{ 
                width: '100%', 
                marginBottom: tokens.spacing[6],
                background: tokens.colors.gradients.accent,
                border: 'none',
                fontSize: tokens.typography.fontSize.base,
                fontWeight: tokens.typography.fontWeight.semibold,
                padding: `${tokens.spacing[4]} ${tokens.spacing[6]}`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <span style={{ position: 'relative', zIndex: 1 }}>
                {loading ? 'Signing In...' : 'Sign In'}
              </span>
              {loading && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                  animation: 'shimmer 1.5s infinite'
                }} />
              )}
            </Button>
            
            <div style={{
              textAlign: 'center',
              paddingTop: tokens.spacing[6],
              borderTop: `1px solid ${tokens.colors.neutral[800]}`
            }}>
              <p style={{
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.sm
              }}>
                Don't have an account?{' '}
                <Link 
                  to="/signup" 
                  style={{
                    color: tokens.colors.accent[400],
                    textDecoration: 'none',
                    fontWeight: tokens.typography.fontWeight.semibold,
                    transition: 'color 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => e.target.style.color = tokens.colors.accent[300]}
                  onMouseLeave={(e) => e.target.style.color = tokens.colors.accent[400]}
                >
                  Create an account
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Add keyframe animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.05); }
          66% { transform: translate(-20px, 20px) scale(0.95); }
        }
        @keyframes glow {
          0% { filter: drop-shadow(0 0 40px rgba(34, 197, 94, 0.4)); }
          100% { filter: drop-shadow(0 0 60px rgba(34, 197, 94, 0.6)); }
        }
        @keyframes countUp {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

export default LandingPage; 